import { createFileRoute, Link } from "@tanstack/react-router";
import { Wordmark } from "@/components/sox/Wordmark";
import { LEGAL, LEGAL_ROUTES } from "@/lib/legal";
import { openCookiePreferences } from "@/components/sox/CookieConsent";
import { CartProvider, useCart, type Product } from "@/lib/cart";
import { lazy, Suspense } from "react";
// Stripe + checkout UI is a large bundle — keep it off the homepage critical
// path. The chunk is only fetched the first time the cart is actually opened.
const CartSheet = lazy(() =>
  import("@/components/sox/CartSheet").then((m) => ({ default: m.CartSheet })),
);

function LazyCartSheet() {
  const { open } = useCart();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  if (!mounted) return null;
  return (
    <Suspense fallback={null}>
      <CartSheet />
    </Suspense>
  );
}


import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroBox from "@/assets/hero-box.jpg";
import product365Black from "@/assets/product-365-black.jpg";
import product365White from "@/assets/product-365-white.jpg";

const SITE = "https://www.soxie365.com";
const TITLE = "SOXIE365 — 365 Pairs of Premium No-Show Socks, One Box, One Year";
const DESC =
  "Buy 365 pairs of premium black or white no-show socks in one box. Daily fresh cotton socks with VAT and EU-wide shipping included. Never wash, match or lose socks again.";

const SEO_KEYWORDS =
  "365 pairs of socks, bulk socks, socks in bulk, buy socks in bulk, no-show socks, no show socks, best no show socks, black no show socks, white no show socks, premium cotton socks, buy socks online, sock box, yearly socks, daily fresh socks, low cut socks, no-show socks for men, no-show socks for women, never wash socks";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: SEO_KEYWORDS },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/` },
      { property: "og:site_name", content: "SOXIE365" },
      { property: "og:locale", content: "en_US" },
      { property: "og:image", content: `${SITE}/og-image.png` },
      { property: "og:image:alt", content: "SOXIE365 — a matte black box holding 365 pairs of socks" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: `${SITE}/og-image.png` },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${SITE}/#organization`,
              name: "SOXIE365",
              legalName: "SSCS SERVICES LTD",
              url: `${SITE}/`,
              logo: `${SITE}/og-image.png`,
              description: DESC,
              email: "info@soxie365.com",
              address: {

                "@type": "PostalAddress",
                streetAddress: "Amathountos 59, Kypriopoulos Court B",
                addressLocality: "Agios Tychonas, Limassol",
                postalCode: "4532",
                addressCountry: "CY",
              },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "customer support",
                  email: "info@soxie365.com",
                  availableLanguage: ["English"],
                },
              ],

            },
            {
              "@type": "WebSite",
              "@id": `${SITE}/#website`,
              url: `${SITE}/`,
              name: "SOXIE365",
              description: DESC,
              inLanguage: "en",
              publisher: { "@id": `${SITE}/#organization` },
            },
            {
              "@type": "Product",
              "@id": `${SITE}/#product`,
              name: "SOXIE365 — The 365 Pack (365 Pairs of Premium No-Show Socks)",
              description:
                "One box of 365 pairs of premium low-cut no-show socks, all black or all white. A fresh pair every morning for a full year. Made from long-staple cotton with elastane for hidden comfort in sneakers, loafers, and dress shoes.",
              image: [`${SITE}/og-image.png`],
              brand: { "@type": "Brand", name: "SOXIE365" },
              category: "Apparel & Accessories > Clothing > Underwear & Socks > Socks",
              material: "Premium long-staple combed cotton blend with elastane",
              color: ["Black", "White"],
              offers: {
                "@type": "Offer",
                url: `${SITE}/`,
                price: "365.00",
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                itemCondition: "https://schema.org/NewCondition",
                seller: { "@id": `${SITE}/#organization` },
                shippingDetails: {
                  "@type": "OfferShippingDetails",
                  shippingRate: {
                    "@type": "MonetaryAmount",
                    value: "0",
                    currency: "EUR",
                  },
                  shippingDestination: {
                    "@type": "DefinedRegion",
                    name: "EU",
                  },
                },
                hasMerchantReturnPolicy: {
                  "@type": "MerchantReturnPolicy",
                  applicableCountry: "EU",
                  returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
                  merchantReturnDays: 30,
                  returnMethod: "https://schema.org/ReturnByMail",
                  returnFees: "https://schema.org/ReturnShippingFees",
                },
              },
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${SITE}/#breadcrumb`,
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: `${SITE}/`,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "The 365 Pack",
                  item: `${SITE}/#products`,
                },
              ],
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE}/#faq`,
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: IndexPage,
});


const PRODUCTS: Product[] = [
  { id: "p365-black", name: "The 365 Pack —", sub: "365 black pairs · One year", price: 365, priceLabel: "€365", image: product365Black },
  { id: "p365-white", name: "The 365 Pack —", sub: "365 white pairs · One year", price: 365, priceLabel: "€365", image: product365White },

];

function IndexPage() {
  return (
    <CartProvider>
      <div className="bg-background text-foreground font-display">
        <Nav />
        <Hero />
        <Ticker />
        <Benefits />
        <Process />
        <Products />
        <Audit />
        <Ticker reverse />
        <Comparison />
        <Faq />
        <Shipping />
        <FinalCta />
        <Footer />

        <LazyCartSheet />


      </div>
    </CartProvider>
  );
}

/* ---------- Animation primitives ---------- */
function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "section" | "li" | "span" | "h2" | "h3" | "p";
}) {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const revealIfNearViewport = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight + 420 && rect.bottom > -180) {
        el.classList.add("in");
        return true;
      }
      return false;
    };
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add("in");
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.01, rootMargin: "420px 0px" }
    );
    io.observe(el);
    revealIfNearViewport();
    window.addEventListener("scroll", revealIfNearViewport, { passive: true });
    window.addEventListener("resize", revealIfNearViewport);
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", revealIfNearViewport);
      window.removeEventListener("resize", revealIfNearViewport);
    };
  }, []);
  const Comp = Tag as any;
  return (
    <Comp
      ref={ref as any}
      className={`reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Comp>
  );
}


function CountUp({ to, suffix = "", duration = 1600 }: { to: number; suffix?: string; duration?: number }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const start = performance.now();
            const tick = (now: number) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              setVal(Math.round(to * eased));
              if (t < 1) raf = requestAnimationFrame(tick);
            };
            raf = requestAnimationFrame(tick);
            io.disconnect();
          }
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [to, duration]);
  return <span ref={ref}>{val.toLocaleString()}{suffix}</span>;
}

function Ticker({ reverse = false }: { reverse?: boolean }) {
  const words = [
    "365 FRESH STARTS",
    "ZERO LAUNDRY",
    "ONE BOX",
    "DAILY FRESH SOCKS",
    "SHIPS ACROSS THE EU",
    "PREMIUM COTTON",
    "NEVER MATCH AGAIN",
    "WAKE · WEAR · WIN",
  ];
  const row = [...words, ...words, ...words];
  return (
    <div className="w-full overflow-hidden border-y border-black/10 bg-accent py-3.5 text-black select-none lg:py-4">
      <div className="w-full overflow-hidden">
        <div className={`flex w-max gap-10 whitespace-nowrap lg:gap-12 ${reverse ? "animate-marquee-reverse" : "animate-marquee"}`}>
          {row.map((w, i) => (
            <span key={i} className="flex shrink-0 items-center gap-10 text-xl font-extrabold uppercase tracking-tighter sm:text-2xl md:text-3xl lg:gap-12">
              {w}
              <span className="size-3 shrink-0 rounded-full bg-black" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function Nav() {
  const { count, setOpen } = useCart();
  return (
    <nav className="sticky top-0 z-50 border-b border-black/5 bg-white/90 px-5 py-4 backdrop-blur-md sm:px-6 lg:px-8">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
        <a href="#top" aria-label="SOXIE365 home" className="group flex min-w-0 items-center gap-2">
          <Wordmark className="h-7 w-auto shrink-0 text-foreground transition-transform duration-300 group-hover:-rotate-3 sm:h-8" />
        </a>
        <div className="flex shrink-0 gap-4 sm:gap-8 items-center">
          <a href="#audit" className="hidden md:inline text-sm font-bold uppercase tracking-widest link-underline">
            The Math
          </a>
          <a href="#process" className="hidden md:inline text-sm font-bold uppercase tracking-widest link-underline">
            Process
          </a>
          <a href="#faq" className="hidden md:inline text-sm font-bold uppercase tracking-widest link-underline">
            FAQ
          </a>
          <button
            onClick={() => setOpen(true)}
            className="bg-black text-white px-5 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 group hover:bg-accent hover:text-black transition-all hover-jitter"
          >
            Cart [{count}]
            <span className="size-2 bg-accent group-hover:bg-black rounded-full animate-pulse" />
          </button>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return <HeroInner />;
}

function ScrambleHeadline({
  phrases,
  className = "",
}: {
  phrases: string[];
  className?: string;
}) {
  const cursor = (
    <span className="ml-2 inline-block h-[0.85em] w-[0.16em] translate-y-[0.1em] bg-accent animate-blink align-middle" />
  );

  return (
    <div className={className}>
      {phrases.map((phrase, i) => {
        const isAccent = phrase.includes("socks");
        const isLast = i === phrases.length - 1;
        return (
          <span key={i} className="block overflow-hidden py-[0.02em]">
            {isLast ? (
              <span className="inline-flex items-center">
                <span
                  className="headline-scan inline-block bg-accent px-[0.25em] py-[0.05em] text-black"
                >
                  {phrase}
                </span>
                {cursor}
              </span>
            ) : (
              <span
                className={`headline-scan block ${isAccent ? "text-accent" : "text-black"}`}
              >
                {phrase}
              </span>
            )}

          </span>
        );
      })}
    </div>
  );
}



function HeroInner() {
  return (
    <section id="top" className="relative flex flex-col overflow-hidden px-5 pb-10 pt-12 sm:px-6 sm:pb-14 sm:pt-16 lg:px-8 lg:pb-16">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 min-[860px]:grid-cols-12 min-[860px]:gap-8 lg:gap-14">
        <div className="min-w-0 animate-slide-up min-[860px]:col-span-7">
          <h1 className="mb-6 text-[clamp(2.5rem,9vw,4.5rem)] font-extrabold uppercase leading-[0.95] tracking-tighter min-[860px]:text-[clamp(3rem,5vw,4rem)] lg:text-[clamp(3.5rem,5.6vw,5.5rem)]">
            <span className="sr-only">Never wash socks again. One box of 365 premium no-show socks.</span>
            <span aria-hidden>
              <ScrambleHeadline
                phrases={["Never wash", "socks again."]}
              />
            </span>
          </h1>
          <p className="mb-8 max-w-md text-base leading-relaxed text-muted-foreground sm:text-lg">
            One box of 365 premium no-show socks. All black or all white. A fresh pair every morning.
          </p>
          <div className="mb-8 flex max-w-md flex-col items-start gap-4 sm:flex-row sm:items-center">
            <a
              href="#products"
              className="inline-flex min-h-12 items-center justify-center gap-3 bg-black px-7 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-accent hover:text-black"
            >
              Get the 365 Pack <span className="font-mono">€365</span>
            </a>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-xs font-extrabold uppercase tracking-wider text-black">
              <span className="size-2 rounded-full bg-black animate-pulse" />
              Free EU shipping
            </span>
          </div>
          <div aria-hidden className="w-full max-w-md">
            <div className="h-1 overflow-hidden bg-black/10">
              <div className="h-full w-full bg-accent animate-bar" />
            </div>
            <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              <span>365 pairs</span>
              <span>0 washes</span>
            </div>
          </div>
        </div>
        <div className="relative min-w-0 animate-slide-up [animation-delay:200ms] min-[860px]:col-span-5">
          <div className="hero-visual group relative mx-auto aspect-square w-full max-w-[36rem] overflow-hidden rounded-[28px] bg-neutral-100 sm:rounded-[36px] min-[860px]:max-w-none lg:rounded-[40px]">
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-accent/20 blur-2xl animate-glow-pulse" />
            <img
              src={heroBox}
              alt="SOXIE365 sealed black box with 365 pairs of premium no-show socks"
              width={1280}
              height={1280}
              fetchPriority="high"
              className="hero-box-image relative h-full w-full object-cover animate-box-sway transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div aria-hidden className="pointer-events-none absolute inset-0 shimmer rounded-[28px] sm:rounded-[36px] lg:rounded-[40px]" />
          </div>
          <div className="absolute bottom-2 right-2 flex size-24 items-center justify-center sm:size-32 min-[860px]:size-24 lg:size-32">
            <div className="absolute inset-0 rounded-full bg-accent shadow-[0_8px_30px_rgba(57,255,20,0.4)] pulse-ring" />
            <svg viewBox="0 0 100 100" className="absolute inset-0 animate-spin-slow text-black">
              <defs>
                <path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
              </defs>
              <text className="font-mono fill-current" fontSize="9" letterSpacing="2">
                <textPath href="#circ">FRESH · DAILY · 365 PAIRS · ONE BOX · </textPath>
              </text>
            </svg>
            <div className="relative z-10 font-mono text-center font-bold leading-none text-black">
              <span className="block text-3xl">365</span>
              <span className="mt-1 block text-[9px] uppercase tracking-tighter">Fresh Starts</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function Audit() {
  const stats: Array<{ to: number; suffix: string; label: string; prefix?: string }> = [
    { to: 80, suffix: "h", label: "Hours a year a typical household spends on laundry sorting (est.)" },
    { to: 12, suffix: "", label: "Single socks a typical drawer collects in a year (est.)" },
    { to: 240, suffix: "", prefix: "€", label: "Yearly detergent and replacement spend (est.)" },
  ];
  return (
    <section id="audit" className="bg-black px-5 py-20 text-white sm:px-6 sm:py-24 lg:px-8 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal as="span" className="font-mono text-accent text-sm mb-4 block">[04] THE AUDIT</Reveal>
        <div className="grid xl:grid-cols-2 gap-14 xl:gap-20">
          <Reveal as="h2" className="text-4xl md:text-5xl font-bold tracking-tighter leading-none uppercase">
            Laundry is a design flaw in your life.
          </Reveal>
          <div className="grid grid-cols-2 gap-8">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 120}>
                <div className="text-5xl md:text-6xl font-extrabold text-accent mb-2">
                  {s.prefix}<CountUp to={s.to} suffix={s.suffix} />
                </div>
                <p className="text-sm text-neutral-400 font-medium uppercase">{s.label}</p>
                <div className="mt-4 h-px bg-accent/40 animate-bar" />
              </Reveal>
            ))}
            <Reveal delay={360}>
              <div className="text-5xl md:text-6xl font-extrabold text-accent mb-2">∞</div>
              <p className="text-sm text-neutral-400 font-medium uppercase">
                Mornings ruined by a single cold heel
              </p>
              <div className="mt-4 h-px bg-accent/40 animate-bar" />
            </Reveal>
          </div>
        </div>
        <p className="mt-10 font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          Figures are illustrative household estimates, not verified research.
        </p>
      </div>
    </section>
  );
}

const STEPS = [
  { tag: "PHASE_01", title: "Open", body: "Crack the seal on your 365-day monolithic vault of premium long-staple cotton." },
  { tag: "PHASE_02", title: "Wear", body: "Slide into a pair that has never known the friction of a dryer. Repeat tomorrow." },
  { tag: "PHASE_03", title: "Recycle", body: "When you're done, use your local textile recycling program where available. SOXIE365 does not run a take-back service." },
];

function Process() {
  return (
    <section id="process" className="px-5 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal as="span" className="font-mono text-sm mb-4 block">[02] THE PROTOCOL</Reveal>
        <Reveal as="h2" className="mb-12 max-w-3xl text-3xl font-extrabold uppercase tracking-tighter sm:text-4xl md:mb-16 md:text-6xl">
          One Box. One Year. <span className="text-black/30">Zero Laundry.</span>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
          {STEPS.map((s, i) => (
            <Reveal key={s.tag} delay={i * 150}>
              <div className="group relative h-full overflow-hidden border border-black p-6 transition-colors hover:bg-accent sm:p-8 lg:p-10 tilt-card">
                <span className="font-mono text-xs block mb-12">{s.tag}</span>
                <h3 className="text-3xl font-bold uppercase mb-4 group-hover:translate-x-1 transition-transform">{s.title}</h3>
                <p className="text-sm font-medium max-w-[85%]">{s.body}</p>
                <span aria-hidden className="pointer-events-none absolute top-6 right-6 text-6xl font-extrabold leading-none text-black/5 group-hover:text-black/15 transition-colors">
                  0{i + 1}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Products() {
  const { add } = useCart();
  const [variant, setVariant] = useState<"black" | "white">("black");
  const p = variant === "black" ? PRODUCTS[0] : PRODUCTS[1];
  return (
    <section id="products" className="bg-neutral-50 px-5 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal className="flex flex-wrap justify-between items-end gap-4 mb-16">
          <div>
            <span className="font-mono text-sm mb-4 block">[03] THE PRODUCT</span>
            <h2 className="text-3xl font-extrabold uppercase tracking-tighter sm:text-4xl md:text-6xl">
              The 365 Pack
            </h2>
            <p className="font-mono text-xs text-black/50 mt-3 uppercase tracking-widest">
              One box · 365 pairs · All black or all white
            </p>
          </div>
          <p className="font-mono text-xs text-black/50">SHIPS ACROSS THE EU · REST OF WORLD COMING SOON</p>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal className="group relative overflow-hidden border border-black/10 bg-white shadow-2xl">
            <div className="absolute left-4 top-4 z-10 rounded-full bg-accent px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-black shadow-lg">
              IN STOCK
            </div>
            <div className="product-visual aspect-square overflow-hidden bg-black p-3 sm:p-4">
              {p.image && (
                <img
                  key={p.id}
                  src={p.image}
                  alt={`SOXIE365 365 Pack — 365 pairs of all ${variant} no-show socks in one box`}
                  width={768}
                  height={768}
                  loading="lazy"
                  className="product-box-image h-full w-full object-contain animate-slide-up transition-transform duration-700 group-hover:scale-[1.025]"
                />
              )}
            </div>
          </Reveal>
          <Reveal delay={120} className="flex flex-col gap-8">
            <div>
              <h3 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tighter">{p.name}</h3>
              <p className="text-sm text-black/50 font-mono mt-2">{p.sub}</p>
            </div>
            <p className="text-lg font-medium text-black/70 max-w-md">
              One box. One year. 365 individually sealed pairs of premium long-staple cotton
              no-show socks — in a single colour, all black or all white. Factory-fresh every
              single morning. No laundry. No matching. No excuses. The simplest way to buy
              socks in bulk online.
            </p>
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-black/40 block mb-3">
                Choose your colour. all black / all white
              </span>
              <div className="flex gap-3">
                {([
                  { key: "black" as const, label: "All Black", swatch: "bg-black" },
                  { key: "white" as const, label: "All White", swatch: "bg-white" },
                ]).map((o) => (
                  <button
                    key={o.key}
                    onClick={() => setVariant(o.key)}
                    aria-pressed={variant === o.key}
                    className={`flex items-center gap-3 px-5 py-3 font-bold uppercase tracking-tight text-sm border transition-all ${
                      variant === o.key
                        ? "border-black bg-black text-white -translate-y-0.5"
                        : "border-black/20 hover:border-black hover:-translate-y-0.5"
                    }`}
                  >
                    <span className={`size-4 rounded-full border border-black/30 ${variant === o.key && o.key === "black" ? "bg-accent" : o.swatch}`} />
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-baseline gap-3">
              <span className="text-5xl font-extrabold font-mono">{p.priceLabel}</span>
              <span className="text-sm font-mono text-black/40">VAT &amp; shipping included</span>
            </div>
            <button
              onClick={() => add(p)}
              className="shimmer bg-black text-white px-10 py-5 font-bold uppercase tracking-tighter text-lg hover:bg-accent hover:text-black transition-all ring-1 ring-black w-fit hover:-translate-y-0.5"
            >
              Add {variant === "black" ? "All Black" : "All White"} to Cart
            </button>
            <ul className="space-y-3 text-sm font-medium text-black/60">
              {["365 individually sealed pairs of no-show socks","One colour per box — all black or all white","Premium long-staple cotton with elastane","Ships across the EU with tracking"].map((line, i) => (
                <Reveal as="li" key={line} delay={200 + i * 80} className="flex items-center gap-3">
                  <span className="size-5 grid place-items-center bg-accent text-black text-xs font-bold rounded-full">✓</span>
                  {line}
                </Reveal>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const BENEFITS = [
  { t: "Never Wash", d: "Zero loads. Zero detergent. Zero static cling." },
  { t: "Never Match", d: "Every pair pre-paired. The orphan sock is extinct." },
  { t: "Never Miss", d: "The dryer demon has nothing left to take from you." },
  { t: "Travel Ready", d: "Toss a week into your carry-on. Leave them at the hotel." },
  { t: "Time Saved", d: "Get 80 hours of your life back every year." },
  { t: "Always Fresh", d: "Factory-new cotton against your skin, daily." },
];

function Benefits() {
  return (
    <section className="px-5 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal as="span" className="font-mono text-sm mb-4 block">[01] WHY IT WORKS</Reveal>
        <Reveal as="h2" className="mb-12 max-w-4xl text-3xl font-extrabold uppercase tracking-tighter sm:text-4xl md:mb-16 md:text-6xl">
          Six reasons your <span className="text-black/30">washing machine</span> will hate us.
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-black">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.t} delay={i * 55} className="border-r border-b border-black">
              <div className="group relative h-full overflow-hidden bg-white p-6 transition-colors hover:bg-accent sm:p-8 lg:p-10">
                <div className="flex items-baseline gap-4 mb-6">
                  <span className="font-mono text-xs text-black/40">0{i + 1}</span>
                  <span className="text-2xl group-hover:rotate-12 inline-block transition-transform">✓</span>
                </div>
                <h3 className="text-2xl font-bold uppercase mb-3 group-hover:translate-x-1 transition-transform">{b.t}</h3>
                <p className="text-sm text-black/70 font-medium max-w-[85%]">{b.d}</p>
                <span aria-hidden className="absolute top-8 right-6 text-5xl font-extrabold leading-none text-black/5 group-hover:text-black/15 transition-colors pointer-events-none">0{i + 1}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const COMPARE = [
  ["Laundry Time", "A Weekly Chore", "None For Socks"],
  ["Lost Socks", "The Usual Few", "Nothing Left To Match"],
  ["Matching Sets", "The Great Search", "Perfect, Always"],
  ["Freshness", "Fades With Washing", "Factory Crisp, Every Day"],
  ["Storage", "An Overflowing Drawer", "One Indexed Box"],
  ["Sustainability", "Often Binned", "Recyclable Where Facilities Exist"],
];

function Comparison() {
  return (
    <section className="px-5 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal as="span" className="font-mono text-sm mb-4 block">[05] THE UPGRADE</Reveal>
        <Reveal as="h2" className="mb-12 text-3xl font-extrabold uppercase tracking-tighter sm:text-4xl md:mb-16 md:text-6xl">
          Side by side. <span className="text-black/30">No contest.</span>
        </Reveal>
        <div className="w-full">
          <div className="grid grid-cols-[1.25fr_1fr_1fr] sm:grid-cols-3 border-b-2 border-black">
            <div className="py-6 pr-3 font-mono text-[10px] sm:text-xs uppercase tracking-widest">Feature</div>
            <div className="py-6 px-2 text-center font-mono text-[10px] sm:text-xs uppercase tracking-widest text-black/50">
              The Old Way
            </div>
            <div className="py-6 px-2 text-center font-mono text-[10px] sm:text-xs uppercase tracking-widest bg-accent">
              SOXIE365
            </div>
          </div>
          {COMPARE.map(([feat, old, sox], i) => (
            <Reveal key={feat} delay={i * 60}>
              <div className="grid grid-cols-[1.25fr_1fr_1fr] sm:grid-cols-3 border-b border-black/10 text-[11px] sm:text-sm hover:bg-neutral-50 transition-colors">
                <div className="py-6 pr-3 font-bold uppercase break-words hyphens-auto">{feat}</div>
                <div className="py-6 px-2 text-center text-black/50 line-through break-words hyphens-auto">{old}</div>
                <div className="py-6 px-2 text-center font-bold bg-accent/15 break-words hyphens-auto">{sox}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const FAQS = [
  {
    q: "What are no-show socks and why 365 pairs?",
    a: "No-show socks sit below the ankle line so they stay hidden inside sneakers, loafers, and low-cut shoes. Our 365 Pack gives you one fresh pair every morning for a full year — 365 individually sealed pairs of premium low-cut no-show socks in one box, all black or all white.",
  },
  {
    q: "Wait — are these actually disposable?",
    a: "They're daily-fresh, not single-use trash. Each pair is built for a full day of wear and made from materials we recommend recycling through your local textile program at end of life.",
  },
  {
    q: "What are they made from?",
    a: "Premium long-staple cotton blend with a touch of elastane for fit — low-cut no-show socks designed for everyday comfort.",
  },
  {
    q: "What colours can I get?",
    a: "Two, and each box is one colour only: an all-black 365 box or an all-white 365 box. No mixed boxes — that's how we keep every pair interchangeable and matching obsolete. Want both? Order one of each.",
  },
  {
    q: "Are they actually recyclable?",
    a: "Yes — the cotton blend is recyclable through most municipal textile recycling programs. We don't run a take-back service ourselves, but we strongly recommend recycling used pairs rather than tossing them in the trash.",
  },
  {
    q: "How many pairs come in the 365 box?",
    a: "Exactly 365 pairs, individually organized and indexed — one for every morning of a standard year.",
  },
  {
    q: "Where do you ship?",
    a: "We currently ship across the EU only — the UK and the rest of the world are coming soon. VAT and shipping are included in the €365 price, so there is nothing extra to pay on delivery.",
  },
  {
    q: "What if they're not comfortable?",
    a: "30-day no-questions return on unopened boxes. If you open it and hate them within the first week, we'll refund you — keep or donate the rest.",
  },
];

function Faq() {
  return (
    <section id="faq" className="bg-neutral-50 px-5 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <span className="font-mono text-sm mb-4 block">[06] THE QUESTIONS</span>
        <h2 className="mb-12 text-3xl font-extrabold uppercase tracking-tighter sm:text-4xl md:mb-16 md:text-6xl">
          Ask us anything.
        </h2>
        <Accordion type="single" collapsible className="w-full max-w-4xl">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border-b border-black/10 last:border-b-0"
            >
              <AccordionTrigger className="text-left text-lg sm:text-xl md:text-2xl font-bold uppercase tracking-tight py-6 gap-4 pr-1 hover:no-underline hover:text-accent">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-base text-black/70 font-medium pb-6 max-w-3xl">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Shipping() {
  const DELIVERY = [
    { region: "EU", time: "3-5 days", note: "Tracked express. Customs included. Free shipping in your €365.", badge: "FREE SHIPPING" },
    { region: "USA & Canada", time: "Coming soon", note: "We're expanding shipping coverage soon." },
    { region: "Rest of World", time: "Coming soon", note: "We're expanding shipping coverage soon." },
  ];

  const POLICY = [
    {
      title: "30-Day Return Window",
      body: "Unopened boxes can be returned within 30 days of delivery for a full refund. The outer box must be intact with its seal unbroken.",
    },
    {
      title: "Refund Method",
      body: "Refunds are issued to your original payment method via Stripe. We process them within 14 days of approving your return; your bank may take a further 5–10 business days to show the credit.",
    },
    {
      title: "Opened Box Guarantee",
      body: "Opened it and it's not for you? Contact us within 7 days. We'll refund you — no need to ship 365 pairs back. We may ask for a few photos.",
    },
    {
      title: "Damaged or Missing",
      body: "If your order arrives damaged, incomplete, or not what you ordered, we replace it free or refund you. We cover return shipping in this case.",
    },
    {
      title: "EU Statutory Right",
      body: "You also have a 14-day right of withdrawal from the day you receive the box. Contact us by email to exercise it; no reason required.",
    },
    {
      title: "How to Start a Return",
      body: "Email info@soxie365.com with your order reference and a brief note. We'll send you a return reference and the correct return address for your region.",
    },
  ];

  return (
    <section id="shipping" className="px-5 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
      <div className="max-w-7xl mx-auto">
        <Reveal as="span" className="font-mono text-sm mb-4 block">[07] THE POLICY</Reveal>
        <Reveal as="h2" className="mb-6 max-w-4xl text-3xl font-extrabold uppercase tracking-tighter sm:text-4xl md:text-6xl">
          Shipping & Returns. <span className="text-black/30">Plain talk.</span>
        </Reveal>
        <Reveal as="p" className="text-lg text-black/60 font-medium max-w-2xl mb-6">
          One price. One delivery. Zero surprises. Your €365 covers everything — the box, the socks, VAT and shipping. No extra charges at checkout, no customs fees on arrival.
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-20">
          {DELIVERY.map((d, i) => (
            <Reveal key={d.region} delay={i * 100}>
              <div className={`group relative h-full overflow-hidden rounded-2xl border border-black/10 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-black hover:shadow-[0_20px_60px_-20px_rgba(0,0,0,.25)] sm:p-8 lg:p-10 ${d.badge ? "bg-black text-white" : "bg-white"}`}>
                {/* Neon slide-in fill on hover */}
                <div className="absolute inset-0 -translate-y-full bg-accent transition-transform duration-300 ease-out group-hover:translate-y-0" />

                <div className="relative z-10">
                  <div className="mb-6 flex items-center justify-between">
                    <h3 className={`text-xl font-bold uppercase tracking-tight transition-colors duration-300 ${d.badge ? "text-white group-hover:text-black" : "group-hover:text-black"}`}>{d.region}</h3>
                    <span className={`size-8 rounded-full text-xs font-bold grid place-items-center transition-colors duration-300 ${d.badge ? "bg-accent text-black group-hover:bg-black group-hover:text-white" : "border border-black/10 bg-accent text-black group-hover:bg-black group-hover:text-white"}`}>
                      0{i + 1}
                    </span>
                  </div>
                  {d.badge && (
                    <span className="inline-block mb-4 rounded-full bg-accent px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-black">
                      {d.badge}
                    </span>
                  )}
                  <span className={`block font-mono text-3xl font-extrabold transition-colors duration-300 ${d.badge ? "text-accent group-hover:text-black" : "text-accent group-hover:text-black"}`}>{d.time}</span>
                  <p className={`mt-4 text-sm font-medium transition-colors duration-300 ${d.badge ? "text-white/70 group-hover:text-black/70" : "text-black/70 group-hover:text-black/70"}`}>{d.note}</p>
                </div>

                {/* Black border ring on hover */}
                <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ boxShadow: 'inset 0 0 0 2px #000' }} />
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal as="h3" className="mb-10 text-2xl font-extrabold uppercase tracking-tighter sm:text-3xl md:text-4xl">
          Return window & refund method. <span className="text-accent">No small print.</span>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black">
          {POLICY.map((p, i) => (
            <Reveal key={p.title} delay={i * 100}>
              <div className="group h-full border-l-4 border-l-accent bg-white p-6 transition-colors hover:bg-neutral-50 sm:p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="size-6 rounded-full bg-accent text-black text-xs font-bold grid place-items-center">
                    {i === 4 ? "14" : "✓"}
                  </span>
                  <h3 className="text-xl font-bold uppercase tracking-tight">{p.title}</h3>
                </div>
                <p className="text-sm text-black/70 font-medium leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={200} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border-2 border-black bg-accent p-6 sm:p-8">
          <div>
            <p className="text-lg font-bold uppercase tracking-tight text-black">Questions about a return?</p>
            <p className="text-sm font-medium text-black/70">Reach out to info@soxie365.com and we'll sort it.</p>
          </div>
          <a href="mailto:info@soxie365.com" className="inline-flex items-center justify-center bg-black text-white px-6 py-3 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors">
            Email Support
          </a>
        </Reveal>
      </div>
    </section>
  );
}


function FinalCta() {

  const { add } = useCart();
  return (
    <section className="relative overflow-hidden bg-black px-5 py-24 text-white sm:px-6 sm:py-32 lg:px-8 lg:py-40">
      <div className="max-w-7xl mx-auto text-center relative">
        <Reveal as="span" className="font-mono text-accent text-sm mb-4 block">[ 09 · THE DECISION ]</Reveal>

        <Reveal as="h2" className="mb-10 text-[clamp(2.5rem,8vw,7rem)] font-extrabold uppercase leading-[0.9] tracking-tighter sm:mb-12">
          Stop Washing. <br />
          <span className="text-accent">Start Wearing.</span>
        </Reveal>
        <Reveal as="p" delay={120} className="max-w-xl mx-auto text-lg text-white/60 mb-12">
          Your last year of laundry is already over. You just haven't picked your box yet —
          365 pairs, all black or all white.
        </Reveal>
        <Reveal delay={240}>
          <button
            onClick={() => add(PRODUCTS[0])}
            className="shimmer relative bg-accent text-black px-12 md:px-16 py-6 md:py-8 text-xl md:text-2xl font-extrabold uppercase tracking-tighter hover:scale-105 transition-transform pulse-ring"
          >
            Get My 365 Pack →
          </button>
        </Reveal>
        <Reveal delay={320}>
          <p className="font-mono text-xs uppercase tracking-widest text-white/40 mt-6">
            Adds the all-black box · Switch to all white in the product section
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="-mt-px bg-black px-5 pb-16 text-white sm:px-6 lg:px-8 lg:pb-20">
      <div className="max-w-7xl mx-auto border-t border-white/10 pt-12 flex flex-col md:flex-row justify-between items-start gap-10">
        <Wordmark className="h-14 w-auto text-white" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-12">
          <div className="space-y-4">
            <p className="font-mono text-xs text-neutral-500">PRODUCT</p>
            <a href="#products" className="block text-sm hover:text-accent">The 365 Pack</a>
            <a href="#process" className="block text-sm hover:text-accent">How It Works</a>
            <a href="#audit" className="block text-sm hover:text-accent">Materials</a>
          </div>
          <div className="space-y-4">
            <p className="font-mono text-xs text-neutral-500">SUPPORT</p>
            <a href="#faq" className="block text-sm hover:text-accent">FAQ</a>
            <a href="#shipping" className="block text-sm hover:text-accent">Shipping</a>

            <a href="mailto:info@soxie365.com" className="block text-sm hover:text-accent">Contact</a>
            <a href="mailto:info@soxie365.com" className="block text-sm text-neutral-400 hover:text-accent">info@soxie365.com</a>

          </div>
          <div className="space-y-4">
            <p className="font-mono text-xs text-neutral-500">LEGAL</p>
            {LEGAL_ROUTES.map((r) => (
              <Link key={r.to} to={r.to} className="block text-sm hover:text-accent">
                {r.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={openCookiePreferences}
              className="block text-sm text-neutral-400 hover:text-accent"
            >
              Cookie settings
            </button>
          </div>
          <div className="space-y-4">
            <p className="font-mono text-xs text-neutral-500">SOCIAL</p>
            <a href="https://www.instagram.com/soxie365/" target="_blank" rel="noreferrer" className="block text-sm hover:text-accent">Instagram</a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-20 text-[10px] font-mono text-neutral-600 flex flex-col sm:flex-row justify-between gap-2 uppercase">
        <span>
          © 2026 SOXIE365 · All rights reserved.
        </span>
        <span>No Laundry. No Excuses. Recycle where facilities exist.</span>
      </div>
    </footer>
  );
}
